import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit
} from "@angular/core";
import { AccountDeviceFacade } from "@youm-front/account-device/data-access";
import { DeviceService } from "@youm-front/device/data-access";
import { MicVolumeService } from "@youm-front/mic-volume/data-access";
import { BehaviorSubject, defer, EMPTY, Subject } from "rxjs";
import {
  catchError,
  concatMap,
  filter,
  map,
  pairwise,
  switchMap,
  takeUntil,
  tap,
  throttleTime
} from "rxjs/operators";
import { ExtendedMediaStream, PlainExtendedMediaStream } from "youm-lib";
// TODO:
// eslint-disable-next-line @nx/enforce-module-boundaries
import { RoomFacade } from "apps/youm-studio/src/app/stores/room-store";

@Component({
  selector: "app-mic-volume",
  templateUrl: "./mic-volume.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MicVolumeComponent implements OnInit, OnDestroy {
  private static VOLUME_LOWER_BOUND = 15;
  private static VOLUME_THROTTLE_TIME_MS = 60 * 1_000;

  private readonly _render$ = new BehaviorSubject<boolean>(false);
  private readonly _stream$ = new BehaviorSubject<null | ExtendedMediaStream>(
    null
  );
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly accountDeviceFacade: AccountDeviceFacade,
    private readonly deviceService: DeviceService,
    private readonly micVolumeService: MicVolumeService,
    private readonly roomFacade: RoomFacade
  ) {}

  ngOnInit() {
    this._stream$
      .pipe(
        pairwise(),
        tap((_) => _[0]?.destroy()),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.roomFacade.localCamMicStream$
      .pipe(
        tap(() => this._render$.next(false)),
        switchMap((_) =>
          _
            ? _.isAudioEnabled$.pipe(
                tap(() => this._render$.next(false)),
                switchMap((_) =>
                  defer(() => (_ ? EMPTY : this.listenMicVolume())).pipe(
                    filter((_) => _ >= MicVolumeComponent.VOLUME_LOWER_BOUND),
                    throttleTime(MicVolumeComponent.VOLUME_THROTTLE_TIME_MS),
                    tap(() => this._render$.next(true))
                  )
                )
              )
            : EMPTY
        ),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    this._stream$.value?.destroy();
    this._stream$.complete();
  }

  get render$() {
    return this._render$.asObservable();
  }

  onClose() {
    this._render$.next(false);
  }

  private listenMicVolume() {
    return this.accountDeviceFacade.audioInputDeviceId$.pipe(
      concatMap((_) =>
        _
          ? this.deviceService
              .getUserMediaAudioInputStreamTrack$({ deviceId: { exact: _ } })
              .pipe(catchError(() => EMPTY))
          : EMPTY
      ),
      map((_) => new PlainExtendedMediaStream(new MediaStream([_]))),
      tap((_) => this._stream$.next(_)),
      switchMap((_) => this.micVolumeService.monitor(_.stream))
    );
  }
}
